import { useCallback, useContext, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { AnalysisQueryHint } from '@/components/presentation';
import { Tooltip } from '@/components/presentation/Tooltip';
import { SelectLocation } from '@/components/Search/Location';
import type { LocationItem } from '@/types/chat.actions';
import { ChatActionType } from '@/types/chat.actions';
import { useBrowserLocation } from '@/utils/hooks/useBrowserLocation';
import { Divider } from '../presentation/Main';
import { BrowserLocationError } from './BrowserLocationError';
import { ActiveChatSessionContext, KolSearchResultsContext, KolSearchResultsSelectionContext, SubmitActionContext } from './context';

type Props = {
  initialLocation?: LocationItem;
};

function getLocationName(location: LocationItem) {
  if (location.type === 'browser') {
    return `(${location.longitude}, ${location.latitude})`;
  } else {
    return location.name;
  }
}

export const GenerateRouteButton = (props: Props) => {
  const chat = useContext(ActiveChatSessionContext);
  const submitAction = useContext(SubmitActionContext);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { selected } = useContext(KolSearchResultsSelectionContext);
  const results = useContext(KolSearchResultsContext);

  const [location, setLocation] = useState<LocationItem | null>(props.initialLocation);
  const [locationError, setLocationError] = useState<string | null>(null);
  const { isSupported, getLocation, state } = useBrowserLocation({ onError: setLocationError, onSuccess: (result) => setLocation({ latitude: result.latitude, longitude: result.longitude, id: 'browser' }) });

  const buildKols = useCallback(() => {
    return selected.filter(kolId => {
      const kol = results.find(r => r.id === kolId);
      return kol && kol?.locationName !== chat.filterOverrides.location?.address;
    }).map(kolId => ({ kolId }));
  }, [chat.filterOverrides.location?.address, results, selected]);

  const onClick = useCallback(() => {
    if (chat.filterOverrides.location) {
      submitAction({
        type: ChatActionType.BuildRoute,
        payload: {
          origin: chat.filterOverrides.location,
          kols: buildKols(),
        },
      }, 'Generate a route with my selected HCPs');
    } else if (errorMessage && location) {
      submitAction({
        type: ChatActionType.BuildRoute,
        payload: {
          origin: {
            latitude: location.latitude,
            longitude: location.longitude,
            address: getLocationName(location),
            kolId: location.type === 'hcp' ? location.id : null,
          },
          kols: buildKols(),
        },
      }, 'Generate a route with my selected HCPs');
    } else {
      setErrorMessage(`No location found`);
    }
  }, [buildKols, chat.filterOverrides.location, errorMessage, location, submitAction]);

  const disabled = useMemo(() => selected.length < 2, [selected.length]);

  const selectedLocation = useMemo(() => {
    if (!location) return null;

    return {
      latitude: location.latitude,
      longitude: location.longitude,
      name: getLocationName(location),
      id: location.type === 'browser' ? `${location.latitude}, ${location.longitude}` : location.id?.toString(),
    };
  }, [location]);

  return (
    <>
      {errorMessage && (
        <StartPointSection>
          <Divider />
          <ErrorContainer>Please select a starting location for your route before generating a route.</ErrorContainer>
          <LocationInputRow>
            <SelectLocation styles={
              {
                container: (baseStyles) => ({ ...baseStyles, width: '400px' }),
              }
            } value={selectedLocation} onChange={setLocation} />
          </LocationInputRow>
          {isSupported &&
            <GetLocationSection>
              <GetLocationButton onClick={getLocation}>Share my current location</GetLocationButton>
              {state === 'error' && <BrowserLocationError errorMessage={locationError} />}
            </GetLocationSection>}
        </StartPointSection>
      )}
      <Tooltip
        title={disabled ? 'Please select at least 2 HCPs to generate a route.' : ''}
        placement="top">
        <HintWrapper>
          <AnalysisQueryHint
            disabled={disabled}
            value={'Generate Route'} onClick={onClick} />
        </HintWrapper>
      </Tooltip>
    </>
  );
};

const StartPointSection = styled.div({
  '> *': {
    marginTop: 10,
  },
});

const LocationInputRow = styled.div({
  display: 'flex',
  gap: 10,
  alignItems: 'center',
});

const HintWrapper = styled.div({
  marginTop: 15,
  display: 'inline-flex',
});

const ErrorContainer = styled.div({
  marginTop: 10,
  fontSize: 14,
});

const GetLocationSection = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: 5,
  marginTop: 5,
});

const GetLocationButton = styled.div(({ theme }) => ({
  cursor: 'pointer',
  color: theme.palette.sentiment.primary.main,
  textDecoration: 'underline',
  fontSize: 14,
}));